import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { http, resolveImage } from "@/lib/api";
import { toast } from "sonner";
import { ShoppingBag, X, Lock, ArrowLeft, ArrowRight, Sparkles, ExternalLink } from "lucide-react";

export default function MarkerShop() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null);

  useEffect(() => {
    http.get("/marker-products")
      .then(({ data }) => setProducts(data || []))
      .catch(() => toast.error("Couldn't load products."))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Layout>
      <section className="bg-[#050505] pt-24 pb-12">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Link to="/truth-markers" className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#8a857d] hover:text-[#C9A055] inline-flex items-center gap-1.5 mb-5">
            <ArrowLeft size={11} /> Back to Truth Markers
          </Link>
          <h1 className="font-display text-4xl sm:text-5xl text-[#F4F1EA] mb-4">Sticker packs &amp; marker kits</h1>
          <p className="font-body text-[15px] leading-[1.8] text-[#B5B0A7]">
            Get the truth into the world. Every pack helps fund more markers, more stories, more reminders.
          </p>
        </div>
      </section>

      <section className="bg-[#0a0a0a] py-16 border-t border-white/5">
        <div className="max-w-5xl mx-auto px-6">
          {loading ? (
            <div className="text-center text-[#8a857d] font-body">Loading…</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="marker-shop-grid">
              {products.map((p) => (
                <ProductCard key={p.slug} p={p} onBuy={() => setBuying(p)} />
              ))}
            </div>
          )}

          <StickerPreview />
        </div>
      </section>

      {buying && <BuyModal product={buying} onClose={() => setBuying(null)} />}
    </Layout>
  );
}

function ProductCard({ p, onBuy }) {
  return (
    <div className="bg-[#050505] border border-white/10 p-5 flex flex-col" data-testid={`marker-product-${p.slug}`}>
      {p.image ? (
        <img src={resolveImage(p.image)} alt={p.name} className="w-full aspect-[4/3] object-cover border border-white/10 mb-4" />
      ) : (
        <div className="w-full aspect-[4/3] border border-white/10 bg-gradient-to-br from-[#1a1814] to-[#050505] flex items-center justify-center mb-4">
          <ShoppingBag size={36} className="text-[#C9A055] opacity-50" />
        </div>
      )}
      <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#C9A055] mb-1">{p.kind}</div>
      <div className="font-display text-lg text-[#F4F1EA]">{p.name}</div>
      <p className="font-body text-[13px] text-[#B5B0A7] mt-2 mb-4 flex-1">{p.description}</p>
      <div className="flex items-center justify-between">
        <div className="font-display text-2xl text-[#C9A055]">${Number(p.price).toFixed(2)}</div>
        <button onClick={onBuy} data-testid={`marker-buy-${p.slug}`} className="btn-gold text-[11px] flex items-center gap-1.5">
          <ShoppingBag size={11} /> Buy
        </button>
      </div>
    </div>
  );
}

function BuyModal({ product, onClose }) {
  const [qty, setQty] = useState(1);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [addr, setAddr] = useState({ line1: "", city: "", state: "", postal_code: "", country: "US" });
  const [customTruth, setCustomTruth] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!name.trim() || !email.trim()) { toast.error("Name and email required."); return; }
    if (product.shippable && (!addr.line1 || !addr.city)) { toast.error("Shipping address needed."); return; }
    setBusy(true);
    try {
      const { data } = await http.post("/marker-products/checkout", {
        product_slug: product.slug, quantity: qty, name, email,
        shipping_address: product.shippable ? addr : null,
        custom_truth: customTruth.trim() || null,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed.");
    } finally { setBusy(false); }
  };

  const total = (Number(product.price) * qty).toFixed(2);

  return (
    <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-[#0a0a0a] border border-white/10 max-w-lg w-full max-h-[92vh] overflow-y-auto" data-testid="marker-buy-modal">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div>
            <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055] mb-1">Order</div>
            <div className="font-display text-xl text-[#F4F1EA]">{product.name}</div>
          </div>
          <button type="button" onClick={onClose} className="text-[#8a857d] hover:text-[#F4F1EA]"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Quantity">
              <input type="number" min={product.qty_min || 1} max="100" value={qty}
                     onChange={(e) => setQty(Math.max(1, Number(e.target.value) || 1))}
                     className="stc-input" data-testid="marker-buy-qty" />
            </Field>
            <Field label="Price">
              <div className="stc-input opacity-70 flex items-center">${Number(product.price).toFixed(2)} each</div>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input className="stc-input" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} data-testid="marker-buy-name" />
            <input className="stc-input" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="marker-buy-email" />
          </div>
          {product.shippable && (
            <div className="space-y-2 border-t border-white/10 pt-3">
              <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#8a857d]">Shipping Address</div>
              <input className="stc-input" placeholder="Street address" value={addr.line1} onChange={(e) => setAddr({ ...addr, line1: e.target.value })} />
              <div className="grid grid-cols-2 gap-2">
                <input className="stc-input" placeholder="City" value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} />
                <input className="stc-input" placeholder="State" value={addr.state} onChange={(e) => setAddr({ ...addr, state: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input className="stc-input" placeholder="ZIP" value={addr.postal_code} onChange={(e) => setAddr({ ...addr, postal_code: e.target.value })} />
                <input className="stc-input" placeholder="Country" value={addr.country} onChange={(e) => setAddr({ ...addr, country: e.target.value })} />
              </div>
            </div>
          )}
          {product.kind === "custom" && (
            <Field label="Custom truth text (we'll design it for you)">
              <textarea className="stc-input min-h-[70px]" maxLength={240} value={customTruth}
                        onChange={(e) => setCustomTruth(e.target.value)} />
            </Field>
          )}
        </div>
        <div className="flex items-center justify-between gap-2 p-5 border-t border-white/10">
          <div className="font-display text-xl text-[#F4F1EA]">${total}</div>
          <button type="submit" disabled={busy} data-testid="marker-buy-submit" className="btn-gold flex items-center gap-2 disabled:opacity-60">
            <Lock size={14} /> {busy ? "Opening…" : "Pay with Stripe"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#8a857d] mb-1.5">{label}</div>
      {children}
    </label>
  );
}

function StickerPreview() {
  const [truth, setTruth] = useState("");
  const [qr, setQr] = useState("https://simpletruthsclub.com/truth-markers");
  const [open, setOpen] = useState(false);

  const preview = () => {
    const t = truth.trim();
    if (t.length < 4) { toast.error("Type a truth first (at least a few words)."); return; }
    const API = process.env.REACT_APP_BACKEND_URL || "";
    const params = new URLSearchParams({ truth: t });
    if (qr.trim()) params.set("qr", qr.trim());
    window.open(`${API}/api/marker-products/sticker.pdf?${params.toString()}`, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="mt-12 pt-12 border-t border-white/10" data-testid="sticker-preview-section">
      <div className="max-w-2xl mx-auto text-center">
        <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055] mb-3 inline-flex items-center gap-2">
          <Sparkles size={11} /> Try Your Own
        </div>
        <h2 className="font-display text-2xl sm:text-3xl text-[#F4F1EA] mb-3">
          Preview a sticker with your truth.
        </h2>
        <p className="font-body text-[14px] text-[#B5B0A7] mb-6">
          See how it looks on the 4×6 marker before you order. The PDF opens in a new tab —
          download it, share it, or hit Buy to order it printed.
        </p>

        {!open ? (
          <button
            onClick={() => setOpen(true)}
            data-testid="sticker-preview-open"
            className="btn-gold inline-flex items-center gap-2"
          >
            Try Your Own Truth <ArrowRight size={14} />
          </button>
        ) : (
          <div className="bg-[#050505] border border-white/10 p-5 text-left max-w-xl mx-auto" data-testid="sticker-preview-form">
            <Field label="Your truth (4–240 chars)">
              <textarea
                data-testid="sticker-preview-truth"
                className="stc-input min-h-[70px]"
                maxLength={240}
                value={truth}
                onChange={(e) => setTruth(e.target.value)}
                placeholder="Sit. Somebody would give anything for one more conversation."
              />
              <div className="text-right text-[10px] text-[#8a857d] mt-1">{truth.length}/240</div>
            </Field>
            <div className="mt-3">
              <Field label="Optional QR code URL (where the sticker links)">
                <input
                  data-testid="sticker-preview-qr"
                  className="stc-input"
                  type="url"
                  placeholder="https://simpletruthsclub.com/truth-markers"
                  value={qr}
                  onChange={(e) => setQr(e.target.value)}
                  maxLength={600}
                />
              </Field>
            </div>
            <div className="flex flex-wrap gap-2 mt-5 justify-end">
              <button onClick={() => { setOpen(false); setTruth(""); setQr(""); }} className="btn-outline text-[11px]">
                Close
              </button>
              <button
                onClick={preview}
                data-testid="sticker-preview-go"
                className="btn-gold text-[11px] flex items-center gap-1.5"
              >
                Open Preview <ExternalLink size={11} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
